import { useState } from "react";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { IoMdArrowBack } from "react-icons/io";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { setNewPrivatePin } from "../../../redux/ApiSlice/AccountPrivateSlice";

export default function SetPassword() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { otp } = useParams();
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [showPin, setShowPin] = useState(false);

    const handleInputChange = (e) => {
        setPassword(e.target.value);
    }

    const handleSubmit = async () => {
        if (password.length !== 6) {
            setError("Password must be exactly 6 digits.");
            toast.error("Password must be exactly 6 digits.");
            return;
        }
        if (isNaN(password)) {
            setError("Password must contain numbers only.");
            toast.error("Password must contain numbers only.");
            return;
        }

        if (!otp) {
            toast.error("Missing OTP. Please restart the process.");
            return;
        }

        try {
            const result = await dispatch(setNewPrivatePin({ otp, new_pin: password }));
            if (result.payload?.error) {
                toast.error(result.payload?.error);
            } else if (result.payload?.message) {
                toast.success(result.payload?.message || "Password set successfully!");
                setError("");
                setTimeout(() => {
                    navigate("/private");
                }, 1000);
            }
        } catch (error) {
            toast.error("Failed to set password. Please try again.");
        }
    };

    return (
        <>
            {/* Header */}
            <div className="sticky top-0 z-10">
                <div className="relative bg-[#BDE6FF] rounded-b-xl h-12 flex items-center justify-center">
                    <IoMdArrowBack
                        onClick={() => navigate("/private")}
                        className="absolute left-2 top-1/2 transform -translate-y-1/2 text-[22px] text-black dark:text-black cursor-pointer"
                    />
                    <p className="font-bold text-[18px] text-black dark:text-black line-clamp-1">
                        Set New Password
                    </p>
                </div>
            </div>

            {/* Main Content */}
            <div className="h-full flex flex-col bg-white dark:bg-[#1E1D21]">
                <div className="flex flex-col items-center justify-center mt-8 px-4">
                    <p className="mb-8 text-center text-sm text-gray-600 dark:text-gray-400 max-w-sm">
                        Create a secure 6-digit PIN to protect your private content. Make sure it's something you'll remember.
                    </p>

                    {/* Form */}
                    <div className="w-full max-w-md space-y-6">
                        {/* Password Field */}
                        <div>
                            <label
                                htmlFor="newPassword"
                                className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
                            >
                                New PIN (6 digits)
                            </label>
                            <div className="relative">
                                <input
                                    type={showPin ? "text" : "password"}
                                    value={password}
                                    onChange={handleInputChange}
                                    maxLength={6}
                                    id="newPassword"
                                    placeholder="Enter 6-digit PIN"
                                    className="w-full h-12 px-4 pr-12 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl outline-none focus:border-blue-500 dark:focus:border-blue-400 transition-colors text-gray-800 dark:text-white placeholder:text-gray-400 text-sm font-medium shadow-sm"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPin(!showPin)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                                >
                                    {showPin ? <AiOutlineEyeInvisible size={20} /> : <AiOutlineEye size={20} />}
                                </button>
                            </div>
                            {error && (
                                <p className="mt-2 text-red-500 text-sm font-medium bg-red-50 dark:bg-red-900/30 px-4 py-2 rounded-lg">
                                    {error}
                                </p>
                            )}
                        </div>

                        {/* Password Strength Indicator */}
                        <div className="space-y-2">
                            <div className="flex justify-between items-center">
                                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                                    PIN Strength
                                </span>
                                <span className="text-xs font-bold text-gray-700 dark:text-gray-300">
                                    {password.length}/6
                                </span>
                            </div>
                            <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                <div
                                    className={`h-full transition-all duration-300 ${password.length === 0
                                        ? 'w-0 bg-gray-400'
                                        : password.length < 4
                                            ? 'bg-red-500'
                                            : password.length < 6
                                                ? 'bg-yellow-500'
                                                : 'bg-green-500'
                                        }`}
                                    style={{ width: `${(password.length / 6) * 100}%` }}
                                />
                            </div>
                        </div>

                        {/* Save Button */}
                        <button
                            onClick={handleSubmit}
                            disabled={password.length !== 6}
                            className={`w-full h-12 rounded-xl flex items-center justify-center gap-2 border border-black font-bold text-[16px] transition-all shadow-lg ${password.length === 6
                                ? 'bg-[#BDE6FF] hover:bg-[#A5D8F5] hover:shadow-xl active:scale-95 text-black cursor-pointer'
                                : 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-500 cursor-not-allowed opacity-60'
                                }`}
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                            </svg>
                            Save Password
                        </button>
                    </div>

                    {/* Tips Box */}
                    <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800 max-w-md">
                        <div className="flex gap-3">
                            <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                            </svg>
                            <div>
                                <p className="text-sm text-gray-700 dark:text-gray-300 font-medium mb-1">
                                    Security Tips
                                </p>
                                <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1 list-disc list-inside">
                                    <li>Avoid using obvious numbers like 123456</li>
                                    <li>Don't use your birthdate or phone number</li>
                                    <li>Keep your PIN private and secure</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
